import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Images, SlidersHorizontal, Bed, LayoutGrid } from 'lucide-react';
import apiClient from '../lib/apiClient';
import { useHotel } from '../context/HotelContext';

// ── Filter definitions ────────────────────────────────────────────────────────
const FILTERS = [
  { key: 'all',     label: 'All Photos',   icon: LayoutGrid,        mediaType: null },
  { key: 'gallery', label: 'Gallery',      icon: Images,            mediaType: 2    },
  { key: 'slider',  label: 'Hotel Views',  icon: SlidersHorizontal, mediaType: 1    },
  { key: 'rooms',   label: 'Rooms',        icon: Bed,               mediaType: 0    },
];

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ images, index, onClose }) => {
  const [current, setCurrent] = useState(index);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  setCurrent(c => Math.max(0, c - 1));
      if (e.key === 'ArrowRight') setCurrent(c => Math.min(images.length - 1, c + 1));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  const img = images[current];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-colors"
      >
        <X size={22} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm tracking-wider">
        {current + 1} / {images.length}
      </div>

      {/* Prev */}
      {current > 0 && (
        <button
          onClick={e => { e.stopPropagation(); setCurrent(c => c - 1); }}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Image */}
      <img
        src={img.src}
        alt={img.title}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      />

      {/* Next */}
      {current < images.length - 1 && (
        <button
          onClick={e => { e.stopPropagation(); setCurrent(c => c + 1); }}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Caption */}
      {img.title && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center px-4">
          <p className="text-white/80 text-sm">{img.title}</p>
        </div>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={e => { e.stopPropagation(); setCurrent(i); }}
              className={`rounded-full transition-all ${i === current ? 'w-4 h-2 bg-white' : 'w-2 h-2 bg-white/30'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Skeleton grid ─────────────────────────────────────────────────────────────
const GallerySkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="animate-pulse bg-gray-200 rounded-xl"
        style={{ aspectRatio: i % 5 === 0 ? '1/1.3' : i % 3 === 0 ? '1/0.75' : '1/1' }}
      />
    ))}
  </div>
);

// ── Gallery page ──────────────────────────────────────────────────────────────
const Gallery = () => {
  const { hotel } = useHotel();
  const [allImages, setAllImages] = useState([]);       // { src, title, category }
  const [loading, setLoading]     = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const primaryColor = hotel?.primaryColor || '#2563eb';

  // Fetch all image types in parallel
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [galleryRes, sliderRes, roomsRes] = await Promise.allSettled([
        apiClient.get('/api/File/Files', { params: { mediaType: 2 } }),
        apiClient.get('/api/File/Files', { params: { mediaType: 1 } }),
        apiClient.get('/api/File/Files', { params: { mediaType: 0 } }),
      ]);

      const toImages = (result, category) => {
        if (result.status !== 'fulfilled') return [];
        const files = result.value || [];
        return files.map(f => ({
          src:      f.url || f.src || '',
          title:    f.fileName || hotel?.hotelName || '',
          category,
        })).filter(f => f.src);
      };

      setAllImages([
        ...toImages(galleryRes, 'gallery'),
        ...toImages(sliderRes,  'slider'),
        ...toImages(roomsRes,   'rooms'),
      ]);
    } catch {
      // silently fail — empty gallery shown
    } finally {
      setLoading(false);
    }
  }, [hotel]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Filter images based on active tab
  const filtered = activeFilter === 'all'
    ? allImages
    : allImages.filter(img => img.category === activeFilter);

  // Count per category for badge
  const counts = {
    all:     allImages.length,
    gallery: allImages.filter(i => i.category === 'gallery').length,
    slider:  allImages.filter(i => i.category === 'slider').length,
    rooms:   allImages.filter(i => i.category === 'rooms').length,
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section
        className="relative flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          minHeight: '52vh',
          backgroundImage: hotel?.bannerUrl
            ? `url(${hotel.bannerUrl})`
            : `linear-gradient(135deg, #0f172a 0%, #1e293b 100%)`,
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/65" />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent" />

        <div className="container relative z-10 text-center pt-20 pb-14">
          <p className="text-white/60 text-sm font-medium tracking-widest uppercase mb-3">
            {hotel?.hotelName}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white font-playfair drop-shadow-lg mb-4">
            Our Gallery
          </h1>
          <p className="text-white/75 text-lg max-w-xl mx-auto">
            A visual journey through our spaces, views and experiences
          </p>
          <div className="flex items-center justify-center gap-4 mt-5">
            <div className="h-px w-12 bg-white/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
            <div className="h-px w-12 bg-white/30" />
          </div>
        </div>
      </section>

      {/* ── FILTER TABS ───────────────────────────────────────────────────── */}
      <section className="sticky top-16 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="container">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
            {FILTERS.map(({ key, label, icon: Icon }) => {
              const count = counts[key];
              if (count === 0 && key !== 'all') return null;
              const isActive = activeFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveFilter(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? 'text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={isActive ? { backgroundColor: primaryColor } : {}}
                >
                  <Icon size={15} />
                  {label}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                      isActive ? 'bg-white/25 text-white' : 'bg-white text-gray-500'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── GRID ──────────────────────────────────────────────────────────── */}
      <section className="py-10 md:py-14">
        <div className="container">
          {loading ? (
            <GallerySkeleton />
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <Images className="h-16 w-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">No photos yet</h3>
              <p className="text-gray-400">
                {activeFilter === 'all'
                  ? 'Photos will appear here once uploaded by our team.'
                  : `No ${FILTERS.find(f => f.key === activeFilter)?.label.toLowerCase()} photos yet.`}
              </p>
            </div>
          ) : (
            <>
              {/* Masonry-inspired responsive grid */}
              <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 md:gap-4 space-y-0">
                {filtered.map((img, idx) => (
                  <div
                    key={`${img.category}-${idx}`}
                    className="break-inside-avoid mb-3 md:mb-4 group relative overflow-hidden rounded-xl cursor-pointer bg-gray-100"
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img
                      src={img.src}
                      alt={img.title}
                      loading="lazy"
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500 block"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-300 flex items-end">
                      <div className="w-full p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        {/* Category badge */}
                        <span className="inline-block text-xs font-semibold text-white/90 bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full capitalize">
                          {img.category === 'slider' ? 'Hotel View' : img.category}
                        </span>
                      </div>
                    </div>
                    {/* Zoom icon */}
                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-75 group-hover:scale-100">
                      <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>

              {/* Photo count */}
              <p className="text-center text-gray-400 text-sm mt-8">
                Showing {filtered.length} photo{filtered.length !== 1 ? 's' : ''}
                {activeFilter !== 'all' && ` · ${FILTERS.find(f => f.key === activeFilter)?.label}`}
              </p>
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={filtered}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default Gallery;
